import ProductPhotoViewer from './ProductPhotoViewer';
import ProductDescription from "./ProductDescription";
import ProductPrice from "./ProductPrice";
import { useCurrency } from "../currency/Currency";
import React, { useEffect, useState } from "react";
import { applyPageMeta, productMeta, setJsonLd } from "../seo/pageMeta";
import { productJsonLd } from "../seo/structuredData";
import { Link, useParams, useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { cartItems, UserInfo } from "./atoms/atoms";
import { assertCartEditable } from "../checkout/client";
import useFetch from "../Network/useFetch";
import "../product-detail.css";
import ProductTrust from "./ProductTrust";
import { whatsappLink } from "./MobileContactBar";
import { gaItem, trackEvent } from "../analytics/tracking";
import ProductVideo from "./ProductVideo";
import { PRODUCT_VIDEOS } from "./productVideos";

export default function ProductChildLinks() {
  const { money, currency } = useCurrency();
  const { productId } = useParams();
  const location=useLocation();
  const returnPath=(location.state as any)?.catalogueReturn;
  const catalogueReturn=typeof returnPath==='string'&&/^\/products(?:\?|$)/.test(returnPath)?returnPath:'/products';
  const user = useRecoilValue(UserInfo);
  const [adding, setAdding] = useState(false);
  const [cart, setCart] = useRecoilState(cartItems);
  const [qty, setQty] = useState(1);
  const [optionId, setOptionId] = useState("");
  const [photoOpen,setPhotoOpen]=useState(false);
  const [photo, setPhoto] = useState("");
  const [message, setMessage] = useState("");
  const [failed, setFailed] = useState(false);
  const { data, isLoading, isError, error, refetch } = useFetch(
    `${process.env.REACT_APP_SERVER_URL}/product/byId/${productId}`,
    ["product-detail", productId],
    { staleTime: 0 }
  );
  useEffect(() => {
    setQty(1);
    setOptionId("");
    setPhoto("");
    setPhotoOpen(false);
    setMessage("");
    setFailed(false);
  }, [productId]);
  const item = data?.data?.response;
  // Deleted or unknown products: tell search engines not to index the page,
  // instead of showing a normal page that says the product is unavailable.
  const notFound = !isLoading && (isError ? [400, 404].includes(error?.response?.status) : !!data && !item);
  useEffect(() => {
    if (!item?.name) { applyPageMeta(`/product/${productId}`, notFound ? { title: "Product not found", noindex: true } : { title: "Product details" }); setJsonLd("product", null); return; }
    const meta = productMeta(item);
    const image = [item.image, ...(item.images || [])].filter((x: unknown) => typeof x === "string" && /^https:\/\//.test(x));
    applyPageMeta(`/product/${item._id || productId}`, meta);
    setJsonLd("product", item.tags?.includes("staging-demo") ? null : productJsonLd(item, meta.description || "", image));
  }, [item, productId, notFound]);
  useEffect(() => () => setJsonLd("product", null), []);
  if (isLoading)
    return (
      <main className="pd-page">
        <p role="status">Loading product…</p>
      </main>
    );
  if (isError && !notFound)
    return (
      <main className="pd-page">
        <h1>We couldn’t load this product</h1>
        <button onClick={() => refetch()}>Try again</button>
        <Link to={catalogueReturn}>Back to products</Link>
      </main>
    );
  if (!item)
    return (
      <main className="pd-page">
        <h1>Product unavailable</h1>
        <Link to={catalogueReturn}>Browse products</Link>
      </main>
    );
  const options = Array.isArray(item.option) ? item.option.filter(o => o && typeof o._id === "string" && typeof o.name === "string") : [];
  const hasOptions = Array.isArray(item.option) && item.option.length > 0;
  const selectedOption = options.find(o => o._id === optionId);
  const optionLabel = options.length && options.every(o => /^(red|yellow|pink|blue|white|green|black|orange|purple|warm white|cool white)$/i.test(o.name.trim())) ? "Colour" : "Product option";
  const quoteParams = new URLSearchParams({subject: `Product quote: ${item.name}`.slice(0,150), message: `Product: ${item.name}\n${selectedOption ? `Selected option: ${selectedOption.name}\n` : ''}Product link: https://technogeniousja.com/product/${item._id}\nPlease confirm price and availability.`});
  const optionReady=!!selectedOption?.priceConfirmed && Number.isSafeInteger(selectedOption?.quantity) && selectedOption.quantity>=0 && Number.isFinite(selectedOption.price) && selectedOption.price>0;
  const unitPrice=hasOptions?selectedOption?.price:item.price;
  const stockValue=hasOptions?selectedOption?.quantity:item.totalQantity;
  const stock=Number.isSafeInteger(stockValue)?Math.max(0,stockValue):0;
  const sameLine=(p:any)=>p.id===item._id && (p.optionId||'')===(selectedOption?._id||'');
  const inCart=cart.find(sameLine)?.quantity||0;
  const available=Math.max(0,Math.min(stock-inCart,99-inCart));
  const priceValid=Number.isFinite(unitPrice)&&unitPrice>=0;
  const needsReview=hasOptions?!optionReady:item.tags?.includes('catalog-review')||!priceValid;
  const canBuy=!needsReview&&available>0&&priceValid;
  const sample = item.tags?.includes("staging-demo");
  const photos = Array.from(
    new Set<string>(
      [item.image, ...(item.images || [])].filter(
        (x) => typeof x === "string" && x
      )
    )
  );
  async function add() {
    if (adding) return;
    setAdding(true);
    try {
      await assertCartEditable(user);
      if (!canBuy || !Number.isSafeInteger(qty) || qty < 1 || qty > available)
        throw new Error("Choose a quantity within the available stock.");
      setCart((current) => {
        const existing = current.find(sameLine);
        const quantity = (existing?.quantity || 0) + qty;
        if (quantity > stock || quantity > 99)
          throw new Error("That quantity is more than we have in stock. Choose a smaller quantity.");
        const line = {
          ...item,
          id: item._id,
          _id: item._id,
          quantity,
          price: unitPrice,
          subtotal: quantity * unitPrice,
          name: selectedOption?`${item.name} — ${selectedOption.name}`:item.name,
          ...(selectedOption?{optionId:selectedOption._id,optionName:selectedOption.name}:{}),
          totalQantity:stock,
          Image: item.image,
        };
        return existing
          ? current.map((p) => (sameLine(p) ? line : p))
          : [...current, line];
      });
      setFailed(false);
      setMessage(`${qty} ${qty === 1 ? "item" : "items"} added to your cart.`);
      trackEvent("add_to_cart", { currency: "USD", value: unitPrice * qty, items: [gaItem({ id: item._id, name: item.name, optionName: selectedOption?.name, price: unitPrice, quantity: qty })] });
    } catch (error) {
      setFailed(true);
      setMessage(
        error instanceof Error
          ? error.message
          : "We could not add this item to your cart. Please try again."
      );
    } finally { setAdding(false); }
  }
  return (
    <main className="pd-page">
      <nav aria-label="Breadcrumb">
        <Link to={catalogueReturn}>← All products</Link>
        <span> / Product details</span>
      </nav>
      {sample && (
        <p className="pd-sample">
          STAGING PREVIEW · Sample product and price for testing only. Not a
          sales offer.
        </p>
      )}
      <div className="pd-layout">
        <section aria-label="Product images" className="pd-gallery">
          <button type="button" className="pd-photo-open" disabled={!photos.length} onClick={()=>setPhotoOpen(true)} aria-label="Enlarge product photo">
          <img
            className="pd-main-image"
            src={photo || item.image || "/catalog/placeholder.svg"}
            alt={item.name}
            onError={(e) => {
              e.currentTarget.onerror = null;
              if (!e.currentTarget.src.endsWith("/catalog/placeholder.svg"))
                e.currentTarget.src = "/catalog/placeholder.svg";
            }}
          />
          </button>
          {photos.length>0&&<p className="pd-muted">Select the photo to enlarge it.</p>}
          {photoOpen&&photos.length>0&&<ProductPhotoViewer photos={photos} index={Math.max(0,photos.indexOf(photo||item.image))} name={item.name} onChange={i=>setPhoto(photos[i])} onClose={()=>setPhotoOpen(false)}/>}
          {photos.length > 1 && (
            <div className="pd-thumbnails">
              {photos.map((src, i) => (
                <button
                  key={src}
                  aria-label={`Show product image ${i + 1}`}
                  aria-pressed={(photo || item.image) === src}
                  onClick={() => setPhoto(src)}
                >
                  <img src={src} alt="" />
                </button>
              ))}
            </div>
          )}
        </section>
        <section className="pd-summary" aria-label="Purchase details">
          <p className="pd-eyebrow">
            {item.tags?.find(
              (t) => t !== "staging-demo" && t !== "catalog-review"
            ) || "Technogenious collection"}
          </p>
          <h1>{item.name}</h1>
          <p className="pd-price">
            {selectedOption && Number.isFinite(selectedOption.price) && selectedOption.price > 0
              ? <>{money(selectedOption.price)} {currency}{!selectedOption.priceConfirmed && <small> — subject to confirmation</small>}</>
              : needsReview
              ? "Request a quote"
              : hasOptions
              ? "Choose your configuration with our team"
              : priceValid
              ? <ProductPrice price={item.price} compareAtPrice={item.compareAtPrice}/>
              : "Contact us for pricing"}
            {!needsReview && !hasOptions && priceValid && (
              <span> {currency}</span>
            )}
          </p>
          <p className="pd-stock">
            {needsReview
              ? "Confirm availability"
              : stock > 0
              ? "● In stock"
              : "Currently unavailable"}
          </p>
          {options.length > 0 && <div className="pd-options">
            <label htmlFor="product-option">{optionLabel}</label>
            <select id="product-option" value={optionId} onChange={e => {setOptionId(e.target.value);setQty(1);setMessage("");}} aria-describedby="product-option-help">
              <option value="">Select {optionLabel.toLowerCase()}</option>
              {options.map(option => <option key={option._id} value={option._id}>{option.name}{Number.isFinite(option.price) && option.price > 0 ? ` — ${money(option.price)} ${currency}` : " — Request price"}</option>)}
            </select>
            <p id="product-option-help">Choose an option to see its price and availability. Options with confirmed prices and stock can be added to your cart.</p>
            {selectedOption && <p role="status"><strong>Selected: {selectedOption.name}</strong>{Number.isFinite(selectedOption.price) && selectedOption.price > 0 && <><br />{selectedOption.priceConfirmed ? "Option price" : "Listed option price"}: {money(selectedOption.price)} {currency}{!selectedOption.priceConfirmed && " — subject to confirmation."}</>}</p>}
          </div>}
          <ProductDescription text={item.description} />
          {needsReview ? (
            <div className="pd-notice">
              <p>
                Contact our team to confirm the configuration, price, and
                availability.
              </p>
              {options.length && !selectedOption ? <p>Select an option above to request a quote.</p> : <Link to={`/request-quote?${quoteParams.toString()}`}>Request a product quote ↗</Link>}
            </div>
          ) : (
            <>
              <div className="pd-buy">
                <div>
                  <label htmlFor="product-quantity">Quantity</label>
                  <input
                    id="product-quantity"
                    type="number"
                    min="1"
                    max={Math.max(1, available)}
                    step="1"
                    value={qty}
                    disabled={adding || !canBuy}
                    onChange={(e) => setQty(Number(e.target.value))}
                  />
                </div>
                <button
                  className="pd-primary"
                  disabled={
                    adding || !canBuy ||
                    !Number.isSafeInteger(qty) ||
                    qty < 1 ||
                    qty > available
                  }
                  onClick={add}
                >
                  {stock === 0
                    ? "Out of stock"
                    : available === 0
                    ? "All available stock is in your cart"
                    : "Add to cart"}
                </button>
              </div>
              {inCart > 0 && (
                <p className="pd-muted">
                  {inCart} already in your cart.{" "}
                  <Link to="/cart">View cart →</Link>
                </p>
              )}
            </>
          )}
          {message && (
            <p
              className={failed ? "pd-error" : "pd-success"}
              role={failed ? "alert" : "status"}
            >
              {message} {!failed && <Link to="/cart">View cart →</Link>}
            </p>
          )}
          <a
            className="pd-whatsapp"
            href={whatsappLink(`Hello Technogenious, I'd like to ask about ${item.name}${selectedOption ? ` (${selectedOption.name})` : ""}. https://technogeniousja.com/product/${item._id}`)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Ask about this on WhatsApp ↗
          </a>
          <ProductTrust />
          <div className="pd-help">
            <strong>Find the right fit</strong>
            <p>
              Confirm compatibility with your gaming machine before ordering.
            </p>
            <Link to="/contact">Ask our team ↗</Link>
          </div>
        </section>
      </div>
      <ProductVideo video={PRODUCT_VIDEOS[item._id || productId || ""]} />
      <section className="pd-buying-guide" aria-label="Before you order">
        <div><h2>Product information</h2><p>Availability: <strong>{needsReview ? "Please confirm with our team" : stock > 0 ? `${stock} available` : "Currently unavailable"}</strong></p><p>The specifications are listed above. If something isn’t listed, ask us before ordering.</p></div>
        <div><h2>Compatibility advice</h2><p>Send us your gaming machine model, the part number, and a photo of the existing component. Our team can help you confirm the right fit.</p><a href={`https://wa.me/18764482032?text=${encodeURIComponent(`Hello, please help me confirm compatibility for ${item.name}. Product: https://technogeniousja.com/product/${item._id}`)}`} target="_blank" rel="noopener noreferrer">Ask about this product ↗</a></div>
        <div><h2>Warranty &amp; support</h2><p>Coverage depends on the product or service supplied. Check the applicable terms before purchasing, and keep your invoice for support.</p><Link to="/warranties">Read warranty &amp; returns information →</Link></div>
      </section>
      <section className="pd-delivery">
        <h2>Pickup & delivery</h2>
        <p>
          Choose your delivery option at checkout. Prices are shown in {currency};
          pay by card in USD or bank transfer in USD or JMD.
        </p>
        <dl>
          <div>
            <dt>Store pickup</dt>
            <dd>Free</dd>
          </div>
          <div>
            <dt>Bearer delivery · Kingston &amp; St Andrew</dt>
            <dd>{money(50)}</dd>
          </div>
          <div>
            <dt>Local courier delivery · COD</dt>
            <dd>Courier collects from our store; you pay the courier on delivery</dd>
          </div>
          <div>
            <dt>Knutsford Delivery / Door Way Express · COD</dt>
            <dd>Price determined by the courier, payable on delivery</dd>
          </div>
          <div>
            <dt>International delivery</dt>
            <dd>DHL Express · quoted after you order</dd>
          </div>
        </dl>
      </section>
    </main>
  );
}
